import { Flex } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting } from '@feathr/blackbox';
import { CardV2 as Card } from '@feathr/components';
import {
  AgeDemographics,
  Interests,
  Locations,
  SelectedTargetings,
} from '@feathr/extender/components/Targetings';
import type { ListResponse } from '@feathr/rachis';

import type { IReachValidationResult } from '../AdWizardAudienceStep';
import NarrowingFilterCallout from './AdWizardFiltersStep.NarrowingFilterCallout';

import * as styles from './AdWizardFiltersStep.css';

interface IProps {
  campaign: Campaign;
  targetings: ListResponse<Targeting>;
  /** Reachable-audience forecast for the narrowing-filter callout; omitted by callers without reach. */
  reachValidation?: IReachValidationResult;
}

function AdWizardFiltersStep({
  campaign,
  targetings: targetingsList,
  reachValidation,
}: Readonly<IProps>): JSX.Element {
  const { isOmniCampaign } = campaign;
  const targetings = targetingsList.models;
  const { t } = useTranslation();

  const hasAudienceTargetings = targetings.some((targeting) => !targeting.get('is_archived'));

  return (
    <Flex
      className={styles.root}
      data-name={'filters_step'}
      justify={isOmniCampaign ? 'space-between' : 'center'}
    >
      {isOmniCampaign ? (
        <>
          <div className={styles.left}>
            {reachValidation && (
              <NarrowingFilterCallout
                campaign={campaign}
                hasAudienceTargetings={hasAudienceTargetings}
                reachValidation={reachValidation}
              />
            )}
            <Locations campaign={campaign} targetings={targetings} />
            <AgeDemographics campaign={campaign} targetings={targetings} />
            <Interests campaign={campaign} targetings={targetings} />
          </div>

          <div className={styles.right}>
            <Card>
              <Card.Header title={t('Selected Filters')} />
              <Card.Content addVerticalGap={true}>
                <SelectedTargetings targetings={targetingsList} />
              </Card.Content>
            </Card>
          </div>
        </>
      ) : (
        <Locations campaign={campaign} targetings={targetings} />
      )}
    </Flex>
  );
}

export default observer(AdWizardFiltersStep);
